/**
 * Payment Processor Types
 *
 * Shared types for multi-processor payment support.
 *
 * Supported processors:
 * - USAePay: Legacy payment processor (Lambda-based)
 * - Dejavoo Spin: Physical terminals via cloud API (Fargate-based)
 *
 * Dejavoo Spin API Reference: https://app.theneo.io/dejavoo/spin/spin-rest-api-methods
 */

import { PaymentTypePatient, type PatientPayment } from './PatientPayment.type';

// ============================================================================
// Processor Types and Enums
// ============================================================================

export enum PaymentProcessorType {
  USAePay = 'usaepay',
  Dejavoo = 'dejavoo',
}

export enum DejavooTransactionType {
  Sale = 'sale',
  Auth = 'auth',
  Capture = 'capture',
  Void = 'void',
  Return = 'return',
}

export enum DejavooTransactionStatus {
  Pending = 'pending',
  SentToDevice = 'sent_to_device',
  Processing = 'processing',
  Approved = 'approved',
  Declined = 'declined',
  Error = 'error',
  Timeout = 'timeout',
  Cancelled = 'cancelled',
  Dismissed = 'dismissed',
}

export enum DejavooBatchStatus {
  Open = 'open',
  Closing = 'closing',
  Closed = 'closed',
  Error = 'error',
}

// Dejavoo result codes
export enum DejavooResultCode {
  Success = '0',
  TerminalError = '1',
  APIError = '2',
}

// Common Dejavoo status codes
export enum DejavooStatusCode {
  Approved = '0000',
  TerminalBusy = '1000',
  AuthFailed = '1009',
  MissingRefId = '1010',
  DuplicateRefId = '1011',
  NoOpenBatch = '1020',
  NotConnected = '2001',
  InvalidAuthKey = '2002',
  RegisterNotFound = '2003',
  Timeout = '2007',
  TerminalInUse = '2008',
}

// ============================================================================
// Credential Types
// ============================================================================

export interface DejavooCredentials {
  tpn: string; // Terminal Profile Number (10-12 digits)
  authKey: string; // 10-character authentication key (found in terminal settings)
  registerId?: string; // (Obsolete) Not used in API calls, kept for backwards compatibility
  ecommToken?: string; // JWT token for iPOSpays Freedom to Design (manual entry) - generated in merchant portal
}

export interface DejavooPatientCustomer {
  clinicId: number;
  patientId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

export interface DejavooCustomerMerchant {
  tpn: string;
  ecommToken: string;
}

export interface IposCustomerPreferences {
  eReceipt: boolean;
  customerName: string;
  customerEmail?: string;
  customerMobile?: string;
}

export interface IposConsumerCreateParams {
  firstName: string;
  lastName: string;
  email?: string;
  externalRefId?: string;
}

export interface IposConsumerCreateResponse {
  success: boolean;
  consumerId?: string;
  error?: string;
}

export interface IposConsumerApiResponse {
  consumerData?: { data?: { consumerId?: unknown } };
  consumerId?: unknown;
  data?: { consumerId?: unknown };
}

export interface IposTransactChargeParams extends DejavooCustomerMerchant {
  amount: number;
  cardToken?: string;
  paymentTokenId?: string;
  transactionRefId?: string;
  patient: DejavooPatientCustomer;
  saveCard?: boolean;
  sendReceipt?: boolean;
}

export interface IposTransactChargeResult {
  success: boolean;
  /**
   * True when the processor never answered (timeout, network failure, unparseable
   * body). The charge may still have captured, so `success: false` with this set is
   * NOT a decline — callers that record outcomes must leave the attempt unresolved
   * for reconciliation rather than writing Declined.
   */
  noResponse?: boolean;
  responseCode?: string;
  responseMessage?: string;
  errorMessage?: string;
  rrn?: string;
  authCode?: string;
  cardLastFour?: string;
  cardType?: string;
  cardToken?: string;
  expirationMonth?: string;
  expirationYear?: string;
  consumerId?: string;
  raw: Record<string, unknown>;
}

export interface PaymentProcessorConfigRow {
  id: number;
  clinicId: number;
  locationId?: number | null;
  processor: PaymentProcessorType;
  isActive: boolean;
  isDefault: boolean;
  config: Record<string, unknown> | null;
  createdAt: number;
  updatedAt?: number | null;
}

export interface PaymentProcessorDeviceRow {
  id: number;
  configId: number;
  credentialId?: number | null;
  processor: PaymentProcessorType;
  deviceKey: string;
  deviceName: string;
  deviceType?: string | null;
  status?: string | null;
  locationId?: number | null;
  deviceData?: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number | null;
}

// ============================================================================
// Dejavoo API Request Types
// ============================================================================

export interface DejavooBaseRequest {
  TPN: string;
  AuthKey: string;
  RegisterId?: string;
}

export interface DejavooSaleRequest extends DejavooBaseRequest {
  Amount: string; // Format: "XX.XX"
  TipAmount?: string;
  InvoiceNumber?: string;
  RefId: string; // Unique transaction reference (1-50 chars)
  PaymentType?: DejavooPaymentType;
  PrintReceipt?: '0' | '1' | 'No' | 'Both' | 'Merchant' | 'Customer';
  SigCapture?: '0' | '1';
  GetExtendedData?: '1' | '0';
  SPInProxyTimeout?: number; // 1-720 seconds
  CallbackInfo?: DejavooCallbackInfo;
  CustomFields?: Record<string, string>;
  MerchantNumber?: string; // For multi-merchant environments (1-5)
}

export interface DejavooVoidRequest extends DejavooBaseRequest {
  RefId: string;
}

export interface DejavooReturnRequest extends DejavooBaseRequest {
  Amount: string;
  RefId: string;
  OriginalRefId?: string; // Reference to original transaction
}

export interface DejavooSettleRequest extends DejavooBaseRequest {
  SettleType?: 'Close' | 'Force';
}

export interface DejavooAbortRequest extends DejavooBaseRequest {
  RefId?: string;
}

export interface DejavooCallbackInfo {
  Url: string;
  Headers?: Record<string, string>;
}

export type DejavooPaymentType =
  | 'Credit'
  | 'Debit'
  | 'EBT_Food'
  | 'EBT_Cash'
  | 'Card'
  | 'Cash'
  | 'Check'
  | 'Gift'
  | 'userChoice';

// ============================================================================
// Dejavoo API Response Types
// ============================================================================

export interface DejavooGeneralResponse {
  ResultCode: string; // '0' = Success, '1' = Terminal Error, '2' = API Error
  StatusCode: string; // 4-digit code (0000 = Approved)
  Message?: string;
  DetailedMessage?: string;
  DelayBeforeNextRequest?: number;
}

export interface DejavooTransactionResponse extends DejavooGeneralResponse {
  GeneralResponse?: DejavooGeneralResponse & {
    HostResponseCode?: string;
    HostResponseMessage?: string;
  };
  AuthCode?: string;
  ReferenceId?: string; // SPIn transaction identifier
  RefId?: string; // Echo of request RefId
  SerialNumber?: string;
  BatchNumber?: string;
  TransactionNumber?: string;
  Voided?: boolean;

  // Card data
  CardNumber?: string; // Masked (last 4)
  CardType?: string; // Visa, MC, Amex, etc.
  EntryMethod?: string; // Swipe, Chip, Contactless, Manual
  CardholderName?: string;

  // Amounts
  Amount?: string;
  TipAmount?: string;
  TotalAmount?: string;
  Amounts?: {
    Amount?: number | string | null;
    TipAmount?: number | string | null;
    TotalAmount?: number | string | null;
    FeeAmount?: number | string | null;
    TaxAmount?: number | string | null;
  };

  CardData?: {
    Last4?: string;
    First4?: string;
    CardType?: string;
    EntryType?: string;
    ExpirationDate?: string;
    Name?: string;
  };

  // Tokenization
  IPosToken?: string; // Token for future transactions

  // EMV data
  EMVData?: {
    AID?: string;
    TVR?: string;
    TSI?: string;
    IAD?: string;
    ARC?: string;
  };

  // Receipts
  CustomerReceipt?: string;
  MerchantReceipt?: string;

  // Extended data
  ExtendedDataByApplication?: Record<string, unknown>;
}

export interface DejavooTerminalCardData {
  iPosToken?: string;
  cardLastFour?: string;
  cardType?: string;
  cardholderName?: string;
  expirationMonth?: string;
  expirationYear?: string;
}

export interface DejavooTerminalTokenSaveRequest {
  clinicId: number;
  patientId: string;
  credentialId: number;
  tpn: string;
  consumerId?: string;
  card: DejavooTerminalCardData & {
    iPosToken: string;
    cardLastFour: string;
  };
}

export interface IposTransactionStatusResponse {
  iposhpresponse?: {
    Response?: {
      RespCode?: string;
      AppCode?: string;
      RespMsg?: string;
      TxRefNo?: string;
      RespStatus?: string;
      Status?: string;
      Amount?: string; // Integer minor units in the iPOS status API (`11064` = $110.64).
      TotalAmount?: string; // Integer minor units in the iPOS status API.
      TransactionType?: string;
    };
    TxDetail?: {
      Tpn?: string;
    };
  };
  errors?: Array<{
    field?: string;
    message?: string;
  }>;
}

/**
 * Host verification contract used by the payment server and reconciliation
 * Lambdas. A negative lookup is not proof that a payment is absent unless the
 * processor explicitly confirms that fact.
 */
export interface DejavooHostVerificationStatus {
  approved: boolean;
  absenceConfirmed: boolean;
  transactionType?: string;
  resultCode?: string;
  statusCode?: string;
  message?: string;
  authCode?: string;
  amount?: string;
  voided: boolean;
}

export interface DejavooHostVerificationApiResponse {
  success: boolean;
  error?: string;
  status?: DejavooHostVerificationStatus;
}

export interface DejavooHostVerificationResult {
  verified: boolean;
  approved: boolean;
  absenceConfirmed: boolean;
  transactionType?: string;
  resultCode?: string;
  statusCode?: string;
  amount?: string;
  voided?: boolean;
  message?: string;
}

export type DejavooHostVerificationOptions = {
  batchFallback?: boolean;
  internalToken?: string | null;
};

export type DejavooReconciliationSafetyCode =
  | 'safe'
  | 'missing_payment_data'
  | 'invalid_allocation_data'
  | 'allocation_mismatch'
  | 'allocation_not_payable'
  | 'possible_existing_payment';

export interface DejavooReconciliationSafetyResult {
  safe: boolean;
  code: DejavooReconciliationSafetyCode;
  message: string;
}

export interface DejavooSettleResponse extends DejavooGeneralResponse {
  BatchId?: string;
  BatchNumber?: string;
  TransactionCount?: number;
  TotalAmount?: string;
}

export type DejavooTerminalOutcomeResponse = Partial<DejavooTransactionResponse> &
  Partial<NonNullable<DejavooTransactionResponse['GeneralResponse']>>;

export interface DejavooTerminalStatusResponse extends DejavooGeneralResponse {
  IsConnected?: boolean;
  TerminalStatus?: string;
  SerialNumber?: string;
  FirmwareVersion?: string;
}

// ============================================================================
// Internal Application Types
// ============================================================================

export interface DejavooTransactionRecord {
  id: number;
  clinicId: number;
  locationId?: number | null;
  patientId: string;
  billingKey?: string | null;
  tpn: string;
  registerId?: string | null;
  referenceId?: string | null;
  authCode?: string | null;
  transactionType: DejavooTransactionType;
  amount: number;
  tipAmount?: number;
  status: DejavooTransactionStatus;
  resultCode?: string | null;
  statusCode?: string | null;
  errorMessage?: string | null;
  iPosToken?: string | null;
  cardLastFour?: string | null;
  cardType?: string | null;
  cardholderName?: string | null;
  requestData?: Record<string, unknown> | null;
  responseData?: DejavooTransactionResponse | null;
  batchId?: string | null;
  settledAt?: number | null;
  originalTransactionId?: number | null;
  refunded: boolean;
  voided: boolean;
  reconciling?: string | null;
  createdAt: number;
  updatedAt?: number | null;
  completedAt?: number | null;
}

export interface DejavooBatchRecord {
  id: number;
  clinicId: number;
  locationId?: number | null;
  tpn: string;
  batchId: string;
  batchNumber?: string | null;
  status: DejavooBatchStatus;
  transactionCount: number;
  salesAmount: number;
  refundsAmount: number;
  netAmount: number;
  openedAt: number;
  closedAt?: number | null;
}

export interface DejavooBatchSummary {
  id: string | number;
  batchId: string;
  batchNumber: string | null;
  status: string;
  transactionCount: number;
  salesAmount: number;
  refundsAmount: number;
  netAmount: number;
  openedAt?: number | string | null;
  closedAt?: number | string | null;
  tpn: string;
  configId: number;
  processor: 'dejavoo';
}

export interface DejavooCustomerTokenRecord {
  id: number;
  clinicId: number;
  patientId: string;
  credentialId: number;
  // The manual-entry TPN whose merchant account owns this saved token. This is
  // the visibility and charge-routing boundary for cards on file.
  tpn: string | null;
  // Retained for configuration/admin context. Never use configId to decide
  // whether a saved card belongs to a location; one config can span many TPNs.
  configId?: number | null;
  iPosToken: string;
  cardLastFour: string;
  cardType?: string | null;
  cardholderName?: string | null;
  expirationMonth?: string | null;
  expirationYear?: string | null;
  nickname?: string | null;
  isDefault: boolean;
  // When true, the credit-card processing fee is waived on transactions where
  // this card is selected. See `docs` / migration
  // 20260508120000_add_suppress_processing_fee.sql for context.
  suppressProcessingFee: boolean;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number | null;
  lastUsedAt?: number | null;
  consumerId?: string | null;
}

export interface DejavooPaymentMethod {
  key: string;
  token: string;
  credentialId: number;
  tpn: string | null;
  configId: number | null;
  cardNumber: string;
  cardLastFour: string;
  cardType: string;
  cardholderName: string | null;
  expirationMonth: string | null;
  expirationYear: string | null;
  nickname: string | null;
  isDefault: boolean;
  suppressProcessingFee: boolean;
  processor: 'dejavoo';
  lastUsedAt: number | null;
}

export interface DejavooIposTransactRequest {
  clinicId: number;
  locationId?: number;
  patientId: string;
  cardOwnerPatientId?: string;
  credentialId: number;
  paymentTokenId?: string;
  cardToken?: string;
  storedTokenId?: number;
  amount: number;
  transactionType: 'sale' | 'preauth' | 'void' | 'refund';
  referenceId?: string;
  billingKey?: string;
  saveCard?: boolean;
  cardholderName?: string;
  avsStreet?: string;
  avsPostalCode?: string;
  nickname?: string;
  suppressProcessingFee?: boolean;
  payment?: Partial<PatientPayment>;
}

export type DejavooStoredCardPaymentRequest = Omit<
  Pick<
    DejavooIposTransactRequest,
    | 'clinicId'
    | 'locationId'
    | 'patientId'
    | 'cardOwnerPatientId'
    | 'credentialId'
    | 'cardToken'
    | 'storedTokenId'
    | 'amount'
    | 'billingKey'
    | 'cardholderName'
    | 'payment'
  >,
  'locationId' | 'cardToken' | 'storedTokenId'
> & {
  locationId: number;
  cardToken: string;
  storedTokenId: number;
};

export type DejavooManualPaymentRequest = Omit<
  Pick<
    DejavooIposTransactRequest,
    | 'clinicId'
    | 'locationId'
    | 'patientId'
    | 'credentialId'
    | 'paymentTokenId'
    | 'amount'
    | 'billingKey'
    | 'saveCard'
    | 'cardholderName'
    | 'avsStreet'
    | 'avsPostalCode'
    | 'nickname'
    | 'suppressProcessingFee'
    | 'payment'
  >,
  'locationId' | 'paymentTokenId'
> & {
  locationId: number;
  paymentTokenId: string;
};

// ============================================================================
// Payment Request/Response Types (Processor-agnostic)
// ============================================================================

export interface PaymentSaleRequest {
  clinicId: number;
  locationId?: number;
  patientId: string;
  amount: number;
  tipAmount?: number;
  deviceKey?: string; // For terminal payments
  tokenId?: number; // For stored card payments
  invoiceNumber?: string;
  saveCard?: boolean;
  printReceipt?: boolean;
  captureSignature?: boolean;
  payment?: Partial<PatientPayment>; // Additional payment context
}

export interface PaymentSaleResponse {
  success: boolean;
  transactionId: number;
  processor: PaymentProcessorType;
  referenceId?: string;
  authCode?: string;
  status: DejavooTransactionStatus | string;
  errorMessage?: string;
  cardLastFour?: string;
  cardType?: string;
  token?: string; // If card was tokenized
  // Present only when saveCard was requested. A false value means the charge
  // succeeded but the reusable token was not persisted; callers must not retry
  // the charge and should show cardSaveError to the user.
  cardSaved?: boolean;
  cardSaveError?: string;
  amount: number;
  tipAmount?: number;
  billingKey?: string;
}

export interface PaymentRefundRequest {
  clinicId: number;
  transactionId: number;
  amount?: number; // Partial refund amount (full refund if not specified)
  reason?: string;
  refundMethod?: PaymentTypePatient;
  refundProcessingFee?: boolean;
  // Set when the caller asked for a VOID (handleVoidByBillingKey routing a Spin sale
  // through handleRefund). A settled sale must then be refused with the batch-closed
  // message rather than silently converted into a Return.
  voidIntent?: boolean;
}

export interface PaymentRefundResponse {
  success: boolean;
  transactionId: number;
  referenceId?: string;
  status: string;
  errorMessage?: string;
  amount: number;
}

export interface PaymentVoidRequest {
  clinicId: number;
  transactionId: number;
  reason?: string;
}

export interface PaymentVoidResponse {
  success: boolean;
  transactionId: number;
  referenceId?: string;
  status: string;
  errorMessage?: string;
}

export interface BatchSettleRequest {
  clinicId: number;
  tpn: string;
  forceSettle?: boolean;
}

export interface BatchSettleResponse {
  success: boolean;
  batchId?: string;
  batchNumber?: string;
  transactionCount?: number;
  totalAmount?: number;
  errorMessage?: string;
}
